"use client";

import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useRouter } from "next/navigation";
import { ReactNode, useEffect, useState } from "react";
import Spinner from "./Spinner";

interface CountdownProps {
    date: Date;
    classList?: string,
    refreshOnEnd?: boolean
    endingComponent?: ReactNode
}

export default function Countdown({ date, refreshOnEnd, endingComponent, classList }: CountdownProps) {
    const [loading, setLoading] = useState(true)
    const [timeLeft, setTimeLeft] = useState(() => calculateTimeLeft(date));
    const router = useRouter();

    useEffect(() => {
        function updateTimer() {
            //get new updated state of timer
            const timeRemain = calculateTimeLeft(date);
            setTimeLeft(timeRemain); //set state

            //check for ended
            if (refreshOnEnd && timeRemain == null)
                router.refresh();
        }
        //1 sec interval
        const interval = setInterval(updateTimer, 1000);

        //if first time render timer & hide spinner ASAP
        if (loading) {
            updateTimer(); //get timer data 
            setLoading(false) //change loading state
        }


        //cleanup
        return () => clearInterval(interval);
    }, [date, loading, refreshOnEnd, router]);


    if (!timeLeft)
        return endingComponent || <div className="text-2xl font-bold text-lime-500 text-center">נגמר הזמן</div>;

    const { days, hours, minutes, seconds } = timeLeft;
    return (
        <div
            className={ cn("flex items-start justify-center gap-2 text-center", classList) }
        >

            { loading && <Spinner className="m-auto" /> ||
                <>



                    <TimeUnit value={ seconds } label="שניות" />:
                    <TimeUnit value={ minutes } label="דקות" />
                    { !(hours == 0 && days == 0) && <>:<TimeUnit value={ hours } label="שעות" /></> }
                    { days != 0 && <>:<TimeUnit value={ days } label="ימים" /></> }
                </>
            }
        </div>
    );
};

/** single digit of countdown */
function TimeUnit({ value, label }: { value: number; label: string }) {
    return <div className="flex flex-col items-center ">
        <AnimatePresence mode="wait">
            <motion.b
                className="text-[1em] leading-[1em] font-extrabold"
                key={ value }
                initial={ { opacity: 0, y: "0.5em", scale: 0.5 } } // Start below
                animate={ { opacity: 1, y: 0, scale: 1 } } // Move up into place
                exit={ { opacity: 0, y: "-0.5em", scale: 0.9 } } // Leave upward
                transition={ { duration: 0.2, ease: "easeInOut" } }
            >
                { value.toString().padStart(2, "0") }
            </motion.b>
        </AnimatePresence>

        <span className="text-[0.7em] leading-[0.8em] font-medium text-gray-300">{ label }</span>
    </div>
};

const calculateTimeLeft = (date: Date) => {
    const now = new Date();
    const difference = date.getTime() - now.getTime();

    if (difference <= 0) return null;

    return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / (1000 * 60)) % 60),
        seconds: Math.floor((difference / 1000) % 60),
    };
};